import { useMemo } from 'react';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import type {
  PaymentMetadata,
  ProductData,
  StripeIntegrationSettings
} from '@elfsight/lib-stripe-integration';
import {
  createCheckoutSession,
  usePaymentSessionMetadata
} from '@elfsight/lib-stripe-integration';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';

export function useCheckoutProviderValue({
  customerEmail,
  paymentMetadata,
  stripeProductData: productData,
  handleError
}: {
  customerEmail: string;
  paymentMetadata?: PaymentMetadata;
  stripeProductData: ProductData;
  handleError: () => void;
}) {
  const {
    integrationStripe: { id: integrationId },
    language
  } = useSettings<StripeIntegrationSettings & { language: LanguageValue }>();
  const { language: locale = window.navigator.language } = language ?? {};
  const { setSessionId } = usePaymentSessionMetadata();

  const widgetTokenStore = useWidgetTokenStore();
  const widgetToken = widgetTokenStore.get();

  return useMemo(() => {
    const fetchClientSecret = async () => {
      try {
        const { clientSecret, sessionId } = await createCheckoutSession({
          providerIntegrationId: integrationId,
          customerEmail,
          productData,
          paymentMetadata,
          widgetToken,
          locale
        });

        if (clientSecret && sessionId) {
          setSessionId(sessionId);

          return clientSecret;
        }

        handleError();

        return '';
      } catch (_error) {
        handleError();

        return '';
      }
    };

    const checkoutKey = getCheckoutKey({
      customerEmail,
      productData,
      paymentMetadata,
      integrationId,
      widgetToken
    });

    return { fetchClientSecret, checkoutKey };
  }, [
    customerEmail,
    productData,
    paymentMetadata,
    integrationId,
    widgetToken,
    setSessionId,
    handleError
  ]);
}

function getCheckoutKey({
  integrationId,
  customerEmail,
  productData,
  paymentMetadata,
  widgetToken
}: {
  integrationId: string;
  customerEmail: string;
  paymentMetadata?: PaymentMetadata;
  productData: ProductData;
  widgetToken?: string;
}) {
  try {
    const data = JSON.stringify({
      integration: integrationId,
      email: customerEmail,
      products: productData,
      meta: paymentMetadata,
      token: widgetToken
    });

    return `checkout-${getHash(data)}`;
  } catch (error) {
    console.error(error);

    return `checkout-${Math.random()}`;
  }
}

function getHash(value: string) {
  let hash = 2166136261; // FNV offset basis

  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }

  return hash >>> 0;
}
